import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Table, Tabs, Tab } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
    getTrackingSettings, updateTrackingSettings,
    createTrackingStage, updateTrackingStage, deleteTrackingStage, reorderTrackingStages,
    updateTrackingStatusMapping,
} from '../../../services/TrackingService';

const LANGS = ['nl', 'en', 'ar', 'tr'];
const LANG_LABELS = { nl: 'NL', en: 'EN', ar: 'AR', tr: 'TR' };
const PAGE_TEXT_FIELDS = ['search_title', 'search_prompt', 'result_title', 'not_found_message'];

export default function TrackingSettings() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [prefix, setPrefix] = useState('TAB');
    const [exampleTrackingNumber, setExampleTrackingNumber] = useState('TAB000106');
    const [examplePostcode, setExamplePostcode] = useState('1000 AB');
    const [labelSettings, setLabelSettings] = useState({
        label_width_mm: 100, label_height_mm: 150,
        label_header_font_size: 16, label_address_font_size: 13,
        label_info_font_size: 15, label_barcode_caption_font_size: 16, label_to_font_size: 18,
        label_show_from: true,
        label_from_name_on: true, label_from_name_text: 'Tabliya B.V.',
        label_from_address_on: false, label_from_address_text: 'Disketteweg 14, 1033 NW',
        label_from_email_on: true, label_from_email_text: 'info@tabliya.nl',
    });
    const [stages, setStages] = useState([]);
    const [pageTexts, setPageTexts] = useState({});
    const [statusMapping, setStatusMapping] = useState({});
    const [deliveryStatuses, setDeliveryStatuses] = useState([]);
    const [activeLang, setActiveLang] = useState('nl');
    const [newStageKey, setNewStageKey] = useState('');

    const load = async () => {
        try {
            setLoading(true);
            const res = await getTrackingSettings();
            setPrefix(res.data.tracking_number_prefix || 'TAB');
            setExampleTrackingNumber(res.data.example_tracking_number || 'TAB000106');
            setExamplePostcode(res.data.example_postcode || '1000 AB');
            setLabelSettings({
                label_width_mm: res.data.label_width_mm || 100,
                label_height_mm: res.data.label_height_mm || 150,
                label_header_font_size: res.data.label_header_font_size || 16,
                label_address_font_size: res.data.label_address_font_size || 13,
                label_info_font_size: res.data.label_info_font_size || 15,
                label_barcode_caption_font_size: res.data.label_barcode_caption_font_size || 16,
                label_to_font_size: res.data.label_to_font_size || 18,
                label_show_from: res.data.label_show_from !== false,
                label_from_name_on: res.data.label_from_name_on !== false,
                label_from_name_text: res.data.label_from_name_text ?? 'Tabliya B.V.',
                label_from_address_on: !!res.data.label_from_address_on,
                label_from_address_text: res.data.label_from_address_text ?? 'Disketteweg 14, 1033 NW',
                label_from_email_on: res.data.label_from_email_on !== false,
                label_from_email_text: res.data.label_from_email_text ?? 'info@tabliya.nl',
            });
            setStages(res.data.stages || []);
            setPageTexts(res.data.page_texts || {});
            setStatusMapping(res.data.status_mapping || {});
            setDeliveryStatuses(res.data.delivery_statuses || []);
        } catch {
            toast.error(t('trackingSettings.errors.load'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSavePrefixAndTexts = async () => {
        setSaving(true);
        try {
            await updateTrackingSettings({
                tracking_number_prefix: prefix,
                page_texts: pageTexts,
                example_tracking_number: exampleTrackingNumber,
                example_postcode: examplePostcode,
                ...labelSettings,
            });
            toast.success(t('trackingSettings.success.saved'));
            load();
        } catch {
            toast.error(t('trackingSettings.errors.save'));
        } finally {
            setSaving(false);
        }
    };

    const setPageTextField = (field, lang, value) => {
        setPageTexts((prev) => ({
            ...prev,
            [field]: { ...(prev[field] || {}), [lang]: value },
        }));
    };

    const setStageLabel = (stageId, lang, value) => {
        setStages((prev) => prev.map((s) => (
            s.id === stageId ? { ...s, labels: { ...s.labels, [lang]: value } } : s
        )));
    };

    const handleSaveStage = async (stage) => {
        try {
            await updateTrackingStage(stage.id, { labels: stage.labels });
            toast.success(t('trackingSettings.success.saved'));
        } catch {
            toast.error(t('trackingSettings.errors.save'));
        }
    };

    const handleAddStage = async () => {
        const key = newStageKey.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
        if (!key) return;
        try {
            const res = await createTrackingStage({ key, labels: { nl: key, en: key, ar: key, tr: key } });
            setStages((prev) => [...prev, res.data]);
            setNewStageKey('');
        } catch {
            toast.error(t('trackingSettings.errors.save'));
        }
    };

    const handleDeleteStage = async (stageId) => {
        try {
            await deleteTrackingStage(stageId);
            setStages((prev) => prev.filter((s) => s.id !== stageId));
            setStatusMapping((prev) => {
                const next = { ...prev };
                Object.keys(next).forEach((status) => { if (next[status] === stageId) next[status] = null; });
                return next;
            });
        } catch {
            toast.error(t('trackingSettings.errors.save'));
        }
    };

    const onDragEnd = async (result) => {
        if (!result.destination) return;
        const reordered = Array.from(stages);
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, moved);
        setStages(reordered);
        try {
            await reorderTrackingStages(reordered.map((s) => s.id));
        } catch {
            toast.error(t('trackingSettings.errors.save'));
        }
    };

    const handleMappingChange = async (status, stageId) => {
        const next = { ...statusMapping, [status]: stageId ? Number(stageId) : null };
        setStatusMapping(next);
        try {
            await updateTrackingStatusMapping(next);
        } catch {
            toast.error(t('trackingSettings.errors.save'));
        }
    };

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" />
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <Card className="mb-4">
                        <Card.Header><h4 className="mb-0">{t('trackingSettings.heading')}</h4></Card.Header>
                        <Card.Body>
                            <Form.Group className="mb-3" style={{ maxWidth: 260 }}>
                                <Form.Label>{t('trackingSettings.prefixLabel')}</Form.Label>
                                <Form.Control
                                    value={prefix}
                                    onChange={(e) => setPrefix(e.target.value.toUpperCase())}
                                    maxLength={20}
                                />
                                <Form.Text muted>{t('trackingSettings.prefixHint', { example: `${prefix || 'TAB'}000106` })}</Form.Text>
                            </Form.Group>
                            <Row>
                                <Col md={4}>
                                    <Form.Group className="mb-2">
                                        <Form.Label>{t('trackingSettings.exampleTrackingNumberLabel')}</Form.Label>
                                        <Form.Control
                                            value={exampleTrackingNumber}
                                            onChange={(e) => setExampleTrackingNumber(e.target.value)}
                                            maxLength={30}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-2">
                                        <Form.Label>{t('trackingSettings.examplePostcodeLabel')}</Form.Label>
                                        <Form.Control
                                            value={examplePostcode}
                                            onChange={(e) => setExamplePostcode(e.target.value)}
                                            maxLength={20}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Form.Text muted>{t('trackingSettings.exampleHint')}</Form.Text>
                        </Card.Body>
                    </Card>

                    <Card className="mb-4">
                        <Card.Header><h5 className="mb-0">{t('trackingSettings.labelSettingsHeading')}</h5></Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-2">
                                        <Form.Label>{t('trackingSettings.labelWidth')}</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={40}
                                            max={300}
                                            value={labelSettings.label_width_mm}
                                            onChange={(e) => setLabelSettings((prev) => ({ ...prev, label_width_mm: Number(e.target.value) }))}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-2">
                                        <Form.Label>{t('trackingSettings.labelHeight')}</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={40}
                                            max={300}
                                            value={labelSettings.label_height_mm}
                                            onChange={(e) => setLabelSettings((prev) => ({ ...prev, label_height_mm: Number(e.target.value) }))}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Form.Text muted className="d-block mb-3">{t('trackingSettings.labelSizeHint')}</Form.Text>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-2">
                                        <Form.Label>{t('trackingSettings.labelHeaderFontSize')}</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={8}
                                            max={48}
                                            value={labelSettings.label_header_font_size}
                                            onChange={(e) => setLabelSettings((prev) => ({ ...prev, label_header_font_size: Number(e.target.value) }))}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-2">
                                        <Form.Label>{t('trackingSettings.labelAddressFontSize')}</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={8}
                                            max={48}
                                            value={labelSettings.label_address_font_size}
                                            onChange={(e) => setLabelSettings((prev) => ({ ...prev, label_address_font_size: Number(e.target.value) }))}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-2">
                                        <Form.Label>{t('trackingSettings.labelToFontSize')}</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={8}
                                            max={60}
                                            value={labelSettings.label_to_font_size}
                                            onChange={(e) => setLabelSettings((prev) => ({ ...prev, label_to_font_size: Number(e.target.value) }))}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-2">
                                        <Form.Label>{t('trackingSettings.labelInfoFontSize')}</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={8}
                                            max={48}
                                            value={labelSettings.label_info_font_size}
                                            onChange={(e) => setLabelSettings((prev) => ({ ...prev, label_info_font_size: Number(e.target.value) }))}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-2">
                                        <Form.Label>{t('trackingSettings.labelBarcodeCaptionFontSize')}</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={8}
                                            max={48}
                                            value={labelSettings.label_barcode_caption_font_size}
                                            onChange={(e) => setLabelSettings((prev) => ({ ...prev, label_barcode_caption_font_size: Number(e.target.value) }))}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <hr />
                            <h6>{t('trackingSettings.labelFromHeading')}</h6>
                            <Form.Check
                                type="switch"
                                id="label-show-from"
                                className="mb-2"
                                label={t('trackingSettings.labelShowFrom')}
                                checked={labelSettings.label_show_from}
                                onChange={(e) => setLabelSettings((prev) => ({ ...prev, label_show_from: e.target.checked }))}
                            />
                            <Row className="align-items-center mb-2">
                                <Col md={3}>
                                    <Form.Check
                                        type="switch"
                                        id="label-from-name-on"
                                        label={t('trackingSettings.labelFromName')}
                                        checked={labelSettings.label_from_name_on}
                                        disabled={!labelSettings.label_show_from}
                                        onChange={(e) => setLabelSettings((prev) => ({ ...prev, label_from_name_on: e.target.checked }))}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Form.Control
                                        type="text"
                                        maxLength={200}
                                        value={labelSettings.label_from_name_text}
                                        disabled={!labelSettings.label_show_from || !labelSettings.label_from_name_on}
                                        onChange={(e) => setLabelSettings((prev) => ({ ...prev, label_from_name_text: e.target.value }))}
                                    />
                                </Col>
                            </Row>
                            <Row className="align-items-center mb-2">
                                <Col md={3}>
                                    <Form.Check
                                        type="switch"
                                        id="label-from-address-on"
                                        label={t('trackingSettings.labelFromAddress')}
                                        checked={labelSettings.label_from_address_on}
                                        disabled={!labelSettings.label_show_from}
                                        onChange={(e) => setLabelSettings((prev) => ({ ...prev, label_from_address_on: e.target.checked }))}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Form.Control
                                        type="text"
                                        maxLength={200}
                                        value={labelSettings.label_from_address_text}
                                        disabled={!labelSettings.label_show_from || !labelSettings.label_from_address_on}
                                        onChange={(e) => setLabelSettings((prev) => ({ ...prev, label_from_address_text: e.target.value }))}
                                    />
                                </Col>
                            </Row>
                            <Row className="align-items-center mb-2">
                                <Col md={3}>
                                    <Form.Check
                                        type="switch"
                                        id="label-from-email-on"
                                        label={t('trackingSettings.labelFromEmail')}
                                        checked={labelSettings.label_from_email_on}
                                        disabled={!labelSettings.label_show_from}
                                        onChange={(e) => setLabelSettings((prev) => ({ ...prev, label_from_email_on: e.target.checked }))}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Form.Control
                                        type="text"
                                        maxLength={200}
                                        value={labelSettings.label_from_email_text}
                                        disabled={!labelSettings.label_show_from || !labelSettings.label_from_email_on}
                                        onChange={(e) => setLabelSettings((prev) => ({ ...prev, label_from_email_text: e.target.value }))}
                                    />
                                </Col>
                            </Row>
                            <Form.Text muted>{t('trackingSettings.labelFromHint')}</Form.Text>
                        </Card.Body>
                    </Card>

                    <Card className="mb-4">
                        <Card.Header><h5 className="mb-0">{t('trackingSettings.stagesHeading')}</h5></Card.Header>
                        <Card.Body>
                            <DragDropContext onDragEnd={onDragEnd}>
                                <Table responsive bordered size="sm">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 32 }}></th>
                                            <th>{t('trackingSettings.stageKey')}</th>
                                            {LANGS.map((lang) => <th key={lang}>{LANG_LABELS[lang]}</th>)}
                                            <th style={{ width: 90 }}></th>
                                        </tr>
                                    </thead>
                                    <Droppable droppableId="tracking-stages">
                                        {(provided) => (
                                            <tbody ref={provided.innerRef} {...provided.droppableProps}>
                                                {stages.map((stage, idx) => (
                                                    <Draggable key={stage.id} draggableId={String(stage.id)} index={idx}>
                                                        {(dragProvided, snapshot) => (
                                                            <tr
                                                                ref={dragProvided.innerRef}
                                                                {...dragProvided.draggableProps}
                                                                style={{ ...dragProvided.draggableProps.style, background: snapshot.isDragging ? '#f0f4ff' : undefined }}
                                                            >
                                                                <td {...dragProvided.dragHandleProps} style={{ textAlign: 'center', cursor: 'grab', color: '#adb5bd', verticalAlign: 'middle' }}>
                                                                    <i className="fa-solid fa-grip-vertical" />
                                                                </td>
                                                                <td className="align-middle text-muted small">{stage.key}</td>
                                                                {LANGS.map((lang) => (
                                                                    <td key={lang}>
                                                                        <Form.Control
                                                                            size="sm"
                                                                            dir={lang === 'ar' ? 'rtl' : 'ltr'}
                                                                            value={stage.labels?.[lang] || ''}
                                                                            onChange={(e) => setStageLabel(stage.id, lang, e.target.value)}
                                                                            onBlur={() => handleSaveStage(stages.find((s) => s.id === stage.id))}
                                                                        />
                                                                    </td>
                                                                ))}
                                                                <td className="text-center align-middle">
                                                                    <Button variant="light" size="sm" className="text-danger" onClick={() => handleDeleteStage(stage.id)}>
                                                                        <i className="fa-solid fa-trash" />
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </tbody>
                                        )}
                                    </Droppable>
                                </Table>
                            </DragDropContext>
                            <Row className="align-items-end mt-2">
                                <Col md={4}>
                                    <Form.Control
                                        size="sm"
                                        placeholder={t('trackingSettings.newStagePlaceholder')}
                                        value={newStageKey}
                                        onChange={(e) => setNewStageKey(e.target.value)}
                                    />
                                </Col>
                                <Col md={3}>
                                    <Button variant="outline-primary" size="sm" onClick={handleAddStage} disabled={!newStageKey.trim()}>
                                        <i className="fa-solid fa-plus me-1" /> {t('trackingSettings.addStage')}
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <Card className="mb-4">
                        <Card.Header><h5 className="mb-0">{t('trackingSettings.pageTextHeading')}</h5></Card.Header>
                        <Card.Body>
                            <Tabs activeKey={activeLang} onSelect={(k) => setActiveLang(k)} className="mb-3">
                                {LANGS.map((lang) => (
                                    <Tab eventKey={lang} title={LANG_LABELS[lang]} key={lang}>
                                        {PAGE_TEXT_FIELDS.map((field) => (
                                            <Form.Group className="mb-3" key={field}>
                                                <Form.Label>{t(`trackingSettings.pageTextFields.${field}`)}</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={2}
                                                    dir={lang === 'ar' ? 'rtl' : 'ltr'}
                                                    value={pageTexts[field]?.[lang] || ''}
                                                    onChange={(e) => setPageTextField(field, lang, e.target.value)}
                                                />
                                            </Form.Group>
                                        ))}
                                    </Tab>
                                ))}
                            </Tabs>
                        </Card.Body>
                    </Card>

                    <Card className="mb-4">
                        <Card.Header><h5 className="mb-0">{t('trackingSettings.mappingHeading')}</h5></Card.Header>
                        <Card.Body>
                            <Table responsive bordered size="sm">
                                <thead>
                                    <tr>
                                        <th>{t('tasksPage.columns.deliveryStatus')}</th>
                                        <th>{t('trackingSettings.mappedStage')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {deliveryStatuses.map((status) => (
                                        <tr key={status}>
                                            <td className="align-middle">{t(`deliveryStatuses.${status}`)}</td>
                                            <td>
                                                <Form.Select
                                                    size="sm"
                                                    style={{ maxWidth: 260 }}
                                                    value={statusMapping[status] ?? ''}
                                                    onChange={(e) => handleMappingChange(status, e.target.value)}
                                                >
                                                    <option value="">{t('trackingSettings.noStage')}</option>
                                                    {stages.map((s) => (
                                                        <option key={s.id} value={s.id}>{s.labels?.[activeLang] || s.key}</option>
                                                    ))}
                                                </Form.Select>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>

                    <Button variant="primary" onClick={handleSavePrefixAndTexts} disabled={saving}>
                        {saving ? t('common.saving') : t('common.save')}
                    </Button>
                </div>
            </div>
        </div>
    );
}
